import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { imgVariant, bannerSrcSet } from "@/lib/img";
import { useSettings } from "@/context/SettingsContext";

const DEFAULT_SLIDES = [
  {
    id: "default",
    title: "Entrega em até|24 Horas",
    subtitle: "Pagamento na entrega: PIX, dinheiro ou cartão em até 12x sem juros.",
    disclaimer: "Consulte a disponibilidade do produto e da entrega para sua região.",
    cta_text: "Confira Nossas Ofertas",
    cta_link: "/produtos?promo=1",
    show_avatar: true,
  },
];

// Slide com o conceito principal: caminhão HG + avatar + título em destaque
const TruckSlide = ({ banner, isActive, isFirst }) => {
  const [line1, line2] = (banner.title || "").split("|");
  const Heading = isFirst ? "h1" : "h2";
  return (
    <div className="relative h-[560px] sm:h-[560px] lg:h-[600px] overflow-hidden bg-gradient-to-br from-[#0d63b0] via-[#0B5394] to-[#072f57]">
      {/* caminhão */}
      <img
        src="/assets/hero-truck.webp"
        alt="Caminhão de entregas HG Móveis"
        className="absolute left-0 bottom-0 h-[44%] sm:h-[62%] lg:h-[92%] w-auto object-contain object-left-bottom [mask-image:linear-gradient(to_right,black_72%,transparent_99%)]"
        loading="eager"
      />
      {/* avatar */}
      <img
        src="/assets/avatar.webp"
        alt="Mascote HG Móveis"
        className="absolute right-0 sm:right-2 lg:right-10 bottom-0 h-40 sm:h-60 lg:h-[88%] w-auto object-contain object-bottom drop-shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
        data-testid="hero-avatar"
      />

      {/* conteúdo central */}
      <div className="container-store relative h-full">
        <div className="h-full max-w-2xl mx-auto flex flex-col items-center justify-center text-center px-8 pb-60 sm:pb-24">
          <div className="inline-flex items-center gap-2 bg-yellow-400 rounded-full pl-3 pr-4 py-1.5 mb-3 sm:mb-4 shadow-[0_4px_16px_-4px_rgba(250,204,21,0.6)]">
            <img src="/assets/logo.png" alt="HG Móveis" className="h-6 w-6 sm:h-7 sm:w-7 rounded-full" />
            <span className="text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.14em] text-slate-900">
              Entrega em até 24h*
            </span>
          </div>
          <Heading className="font-heading font-extrabold uppercase tracking-tight leading-[1.02]">
            <span className="block text-white text-3xl sm:text-4xl lg:text-5xl [text-shadow:0_2px_12px_rgba(0,0,0,0.35)]">
              {line1}
            </span>
            <span className="block text-yellow-400 text-5xl sm:text-7xl lg:text-8xl [text-shadow:0_4px_0_rgba(0,0,0,0.18)]">
              {line2 || ""}
            </span>
          </Heading>
          {banner.disclaimer && (
            <p className="mt-2 text-xs sm:text-sm text-blue-100/90 max-w-sm">{banner.disclaimer}</p>
          )}
          {banner.subtitle && (
            <p className="mt-3 sm:mt-4 text-sm sm:text-lg font-semibold text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.4)] max-w-md">
              {banner.subtitle}
            </p>
          )}
          <Link to={banner.cta_link || "/produtos"}>
            <Button
              size="lg"
              className="mt-5 sm:mt-6 rounded-full px-7 sm:px-9 h-11 sm:h-13 bg-yellow-400 hover:bg-yellow-300 text-slate-900 text-sm sm:text-base font-extrabold uppercase tracking-wide shadow-[0_8px_24px_-6px_rgba(250,204,21,0.6)] active:scale-95 transition-transform"
              data-testid="hero-cta-main"
              tabIndex={isActive ? 0 : -1}
            >
              {banner.cta_text} <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Slide padrão: foto de fundo + texto à esquerda
const PhotoSlide = ({ banner, isActive, isFirst }) => {
  const Heading = isFirst ? "h1" : "h2";
  return (
  <div className="relative h-[440px] sm:h-[500px] lg:h-[580px]">
    <picture>
      {banner.image_mobile && (
        <source media="(max-width: 640px)" srcSet={banner.image_mobile} />
      )}
      <img
        src={imgVariant(banner.image_url, "full")}
        srcSet={bannerSrcSet(banner.image_url)}
        sizes="100vw"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        loading={isFirst ? "eager" : "lazy"}
        fetchPriority={isFirst ? "high" : "low"}
        decoding="async"
      />
    </picture>
    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-slate-900/20" />
    <div className="container-store relative h-full flex items-center">
      <div className="max-w-xl lg:max-w-2xl pb-12 px-9 sm:px-0">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full pl-1 pr-3 py-1 mb-4">
          <img src="/assets/logo.png" alt="HG Móveis" className="h-6 w-6 sm:h-7 sm:w-7 rounded-full" />
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-blue-200">
            HG Móveis
          </span>
        </div>
        <Heading className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white uppercase tracking-tight leading-[1.08]">
          {(banner.title || "").replace("|", " ")}
        </Heading>
        <p className="mt-3 sm:mt-4 text-sm sm:text-lg text-slate-200 leading-relaxed">{banner.subtitle}</p>
        <Link to={banner.cta_link || "/produtos"}>
          <Button
            size="lg"
            className="mt-6 rounded-full px-7 sm:px-8 h-11 sm:h-12 text-sm sm:text-base font-semibold active:scale-95 transition-transform"
            tabIndex={isActive ? 0 : -1}
          >
            {banner.cta_text} <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  </div>
  );
};

// Slide de arte pronta: imagem completa, sem textos sobrepostos, sem cortes
const ImageSlide = ({ banner, isActive, isFirst }) => (
  <div className="relative bg-[#06254f] flex items-center justify-center max-h-[640px] overflow-hidden">
    <Link to={banner.cta_link || "/produtos"} tabIndex={isActive ? 0 : -1} data-testid="hero-image-slide-link">
      <picture>
        {banner.image_mobile && (
          <source media="(max-width: 640px)" srcSet={banner.image_mobile} />
        )}
        <img
          src={imgVariant(banner.image_url, "full")}
          srcSet={bannerSrcSet(banner.image_url)}
          sizes="100vw"
          alt={banner.title || "Banner promocional HG Móveis"}
          className="w-full h-auto max-h-[640px] object-contain"
          loading={isFirst ? "eager" : "lazy"}
          fetchPriority={isFirst ? "high" : "low"}
          decoding="async"
        />
      </picture>
    </Link>
  </div>
);

export const Hero = ({ banners }) => {
  const settings = useSettings();
  // transições/autoplay dos banners — desligável no painel (Animações e Efeitos) ou por movimento reduzido
  const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const animOn = !reduceMotion && (settings.animations || {}).enabled !== false && (settings.animations || {}).banner_transitions !== false;
  const slides = banners && banners.length > 0 ? banners : DEFAULT_SLIDES;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: animOn ? 32 : 0 });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi]);

  // reinit quando os banners chegam da API (carrossel começa com o slide padrão)
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    setSnaps(emblaApi.scrollSnapList());
  }, [emblaApi, slides.length]);

  // autoplay com reinício após interação manual (desligado se as transições estiverem off)
  useEffect(() => {
    if (!emblaApi || slides.length < 2 || !animOn) return;
    let timer = setInterval(() => emblaApi.scrollNext(), 5500);
    const restart = () => {
      clearInterval(timer);
      timer = setInterval(() => emblaApi.scrollNext(), 5500);
    };
    emblaApi.on("pointerDown", restart);
    return () => {
      clearInterval(timer);
      emblaApi.off("pointerDown", restart);
    };
  }, [emblaApi, slides.length, animOn]);

  return (
    <section className="relative bg-slate-900" data-testid="hero-carousel" data-anim="banner">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {slides.map((banner, i) => (
            <div
              key={banner.id || i}
              className="relative flex-[0_0_100%] min-w-0"
              data-testid={`hero-slide-${i}`}
              aria-hidden={i !== selected}
            >
              {banner.full_image ? (
                <ImageSlide banner={banner} isActive={i === selected} isFirst={i === 0} />
              ) : banner.show_avatar ? (
                <TruckSlide banner={banner} isActive={i === selected} isFirst={i === 0} />
              ) : (
                <PhotoSlide banner={banner} isActive={i === selected} isFirst={i === 0} />
              )}
            </div>
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button
            onClick={scrollPrev}
            aria-label="Banner anterior"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition-colors"
            data-testid="hero-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Próximo banner"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition-colors"
            data-testid="hero-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2" data-testid="hero-dots">
            {snaps.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                aria-label={`Ir para banner ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === selected ? "w-6 bg-white" : "w-2 bg-white/40 hover:bg-white/70"
                }`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
